import { type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui/layout/layout';
import { ROUND_ICON_BUTTON_EDGE_INSET } from '@/design-system-v2/round-icon-button';
import { cn } from '@/lib/utils';

interface SidebarSectionHeadingProps extends React.HTMLAttributes<HTMLDivElement> {
  rightSlot?: ReactNode;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  onNavigate?: React.MouseEventHandler<HTMLButtonElement>;
  titleClassName?: string;
}

export const SidebarSectionHeading = ({
  className,
  children,
  rightSlot,
  isExpanded,
  onExpandedChange,
  onNavigate,
  titleClassName,
  ...props
}: SidebarSectionHeadingProps) => {
  const headingLabel = typeof children === 'string' ? children : 'section';

  return (
    <div
      className={cn('group/sidebar-section-heading w-full', className)}
      {...props}
    >
      <XStack className="min-h-7 items-center gap-1 px-[var(--row-pad)]">
        <XStack className="min-w-0 flex-1 items-center gap-1">
          {onNavigate && (
            <button
              type="button"
              className={cn(
                'min-w-0 truncate rounded-sm text-left text-sm text-foreground-muted transition-colors hover:text-foreground focus-ring',
                titleClassName
              )}
              onClick={onNavigate}
            >
              {children}
            </button>
          )}
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${headingLabel}`}
            className={cn(
              'flex min-w-0 items-center gap-1 rounded-sm text-left text-sm text-foreground-muted',
              onNavigate
                ? 'size-6 shrink-0 justify-center focus-ring-inset'
                : 'flex-1 focus-ring',
              'transition-colors hover:text-foreground',
              titleClassName
            )}
            onClick={() => onExpandedChange(!isExpanded)}
          >
            {!onNavigate && <span className="truncate">{children}</span>}
            <Icons.chevronDown
              aria-hidden
              className={cn(
                'size-3.5 shrink-0 opacity-0 transition-[opacity,transform] duration-150',
                // on touch the chevron stays, so expand/collapse stays reachable without hover
                'touch:opacity-100',
                'group-hover/sidebar-section-heading:opacity-100 group-has-[:focus-visible]/sidebar-section-heading:opacity-100',
                !isExpanded && '-rotate-90 opacity-100'
              )}
            />
          </button>
        </XStack>
        {rightSlot ? (
          <XStack
            className={cn(
              'shrink-0 items-center gap-0.5 opacity-0 transition-opacity duration-150 [&_button:focus-visible]:-outline-offset-2',
              ROUND_ICON_BUTTON_EDGE_INSET.xs,
              'touch:opacity-100',
              'group-hover/sidebar-section-heading:opacity-100 group-has-[:focus-visible]/sidebar-section-heading:opacity-100',
              'has-[[data-state=open]]:opacity-100',
              'has-[[data-active=true]]:opacity-100'
            )}
          >
            {rightSlot}
          </XStack>
        ) : null}
      </XStack>
    </div>
  );
};
